import { useEffect } from 'react'

import { Consultation } from '@/core/models/consultation.model'
import { Room } from '@/core/models/room.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  addUserToRoom,
  createRoom,
  enterEmergencyRoomSocketEvent,
  getRoom,
  leaveEmergencyRoomSocketEvent,
  updateRoom,
} from '@/redux/reducers/room.reducer'

import { isStaff } from '@/utils/type-guards'

type UseEmergencyRoomLifecycleParams = {
  consultationRoom?: Room
  emergencyConsultation?: Consultation
  emergencyRoom: Room | null
  newlyCreatedConsultationId?: string
  roomId?: string
}

export function useEmergencyRoomLifecycle({
  consultationRoom,
  emergencyConsultation,
  emergencyRoom,
  newlyCreatedConsultationId,
  roomId,
}: UseEmergencyRoomLifecycleParams) {
  const dispatch = useAppDispatch()
  const authUser = useAppSelector(selectAuthUser)

  // Get or create PDS's emergency room
  useEffect(() => {
    if (!authUser || !isStaff(authUser) || authUser.status !== 'PDS') return

    if (!authUser.emergencyRoom) {
      dispatch(createRoom({ creator: authUser._id, status: 'available' }, true))
      return
    }

    dispatch(getRoom(typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom._id))
  }, [dispatch, authUser])

  // Get emergency room via roomId param (doctor entering room)
  useEffect(() => {
    if (!roomId) return

    dispatch(getRoom(roomId))
    dispatch(enterEmergencyRoomSocketEvent(roomId))

    return () => {
      dispatch(leaveEmergencyRoomSocketEvent(roomId))
    }
  }, [dispatch, roomId])

  // Restore correct room status when PDS returns
  useEffect(() => {
    if (!isStaff(authUser) || authUser?.status !== 'PDS' || !emergencyRoom) return

    let status: Room['status'] | undefined

    if (emergencyRoom.status === 'inactive' && emergencyRoom.currentConsultation) {
      status = 'standby'
    }

    if (emergencyRoom.status !== 'available' && !emergencyRoom.currentConsultation) {
      status = 'available'
    }

    if (status) {
      dispatch(updateRoom(emergencyRoom._id, { status }))
    }
  }, [authUser, dispatch, emergencyRoom])

  // Update room status based on doctor presence
  useEffect(() => {
    if (!isStaff(authUser) || authUser?.status !== 'PDS' || !emergencyRoom || !emergencyConsultation) return

    if (emergencyRoom.status === 'standby' && emergencyConsultation.medecin) {
      dispatch(updateRoom(emergencyRoom._id, { status: 'in_progress' }))
    }

    if (emergencyRoom.status === 'in_progress' && !emergencyConsultation.medecin) {
      dispatch(updateRoom(emergencyRoom._id, { status: 'standby' }))
    }
  }, [authUser, dispatch, emergencyConsultation, emergencyRoom])

  // Get consultation room
  useEffect(() => {
    if (consultationRoom || !emergencyRoom?.currentConsultation) return

    dispatch(getRoom(emergencyRoom.currentConsultation))
  }, [consultationRoom, dispatch, emergencyRoom])

  // Link newly created consultation to emergency room
  useEffect(() => {
    if (
      !isStaff(authUser) ||
      authUser?.status !== 'PDS' ||
      !newlyCreatedConsultationId ||
      !emergencyRoom ||
      emergencyRoom.currentConsultation
    ) {
      return
    }

    dispatch(updateRoom(emergencyRoom._id, { currentConsultation: newlyCreatedConsultationId, status: 'standby' }))
  }, [authUser, dispatch, emergencyRoom, newlyCreatedConsultationId])

  // Add non-creator user to room
  useEffect(() => {
    if (
      !consultationRoom ||
      !authUser ||
      authUser._id === (typeof consultationRoom.creator === 'string' ? consultationRoom.creator : consultationRoom.creator._id) ||
      !emergencyRoom?.currentConsultation
    ) {
      return
    }

    dispatch(addUserToRoom(emergencyRoom.currentConsultation))
  }, [authUser, consultationRoom, dispatch, emergencyRoom])

  // Set room to inactive when PDS leaves
  useEffect(() => {
    return () => {
      if (authUser && isStaff(authUser) && authUser.status === 'PDS' && authUser.emergencyRoom) {
        const emergencyRoomId = typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom._id
        dispatch(updateRoom(emergencyRoomId, { status: 'inactive' }))
      }
    }
  }, [authUser, dispatch])
}
